import type { FetchResult } from 'apollo-link';
import { ApolloLink, Observable } from 'apollo-link';

export const SCHEMA_HASH_STORAGE_KEY = 'graphqlSchemaHash';

const RELOAD_ATTEMPTED_KEY = 'graphqlSchemaHashReload';
const APOLLO_CACHE_PERSIST_KEY = 'apollo-cache-persist';

export const syncSchemaHash = (args: { schemaHash: string; storage: Pick<Storage, 'getItem' | 'setItem'> }) => {
  const stored = args.storage.getItem(SCHEMA_HASH_STORAGE_KEY);
  if (stored === args.schemaHash) return 'match' as const;
  args.storage.setItem(SCHEMA_HASH_STORAGE_KEY, args.schemaHash);
  return stored ? ('stale' as const) : ('persist' as const);
};

const hasSchemaValidationError = (errors: FetchResult['errors']) =>
  errors?.some(
    (error) => error.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' || error.message.startsWith('Cannot query field ')
  ) ?? false;

const reloadStaleClient = async () => {
  const registrations = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistrations() : [];
  const cacheKeys = 'caches' in window ? await caches.keys() : [];
  await Promise.all([
    ...registrations.map((registration) => registration.unregister()),
    ...cacheKeys.map((key) => caches.delete(key)),
  ]);
  window.localStorage.removeItem(APOLLO_CACHE_PERSIST_KEY);
  window.location.reload();
};

const inspectResponse = (response: FetchResult) => {
  const schemaHash = typeof response.extensions?.schemaHash === 'string' ? response.extensions.schemaHash : null;
  const storedState = schemaHash ? syncSchemaHash({ schemaHash, storage: window.localStorage }) : ('unknown' as const);

  if (storedState !== 'stale' || !hasSchemaValidationError(response.errors)) return response;
  if (process.env.NODE_ENV !== 'production') return response;
  if (window.sessionStorage.getItem(RELOAD_ATTEMPTED_KEY)) return response;

  window.sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, '1');
  void reloadStaleClient();
  return { ...response, errors: undefined };
};

export const schemaHashLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next(response) {
          observer.next(inspectResponse(response));
        },
        error(error) {
          observer.error(error);
        },
        complete() {
          observer.complete();
        },
      });
      return () => subscription.unsubscribe();
    })
);
